"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { METRICS_EVENTS } from "@cede/types";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useRecentlyCancelledStreaks, useWhitelistedCampaigns } from "@/hooks/notifications";
import { cn } from "@/lib/utils";
import { useNotificationState } from "@/store/notificationStore";
import { Bell } from "lucide-react";

interface NotificationBellProps {
  onClick: () => void;
  className?: string;
}

/**
 * NotificationBell - Bell icon with dot indicator for exclusive campaign notifications.
 *
 * Features:
 * - Shows dot indicator when there are unseen exclusive campaigns or cancelled streaks
 * - CSS wobble animation for attention-grabbing effect
 * - Tooltip that shows once per session for new notifications
 * - Tracks Mixpanel events for bell views and clicks
 *
 * TODO: This is a VERY naive inbox system implementation. We should implement a more scalable system in the API if more notification types should be shown in this component
 */
export const NotificationBell = ({ onClick, className }: NotificationBellProps) => {
  const { trackEvent } = useMetricsEvents();
  const { whitelistedCampaigns } = useWhitelistedCampaigns();
  const { cancelledStreaks } = useRecentlyCancelledStreaks();
  const { hasUnseenCampaigns, shouldShowTooltip, markTooltipShown } = useNotificationState();

  // Use campaign `id` as string for notification tracking
  const campaignIds = useMemo(() => whitelistedCampaigns.map((c) => String(c.id)), [whitelistedCampaigns]);
  const cancelledStreakIds = useMemo(() => cancelledStreaks.map((s) => s.id), [cancelledStreaks]);
  const allNotificationIds = useMemo(() => [...campaignIds, ...cancelledStreakIds], [campaignIds, cancelledStreakIds]);

  const hasUnseen = hasUnseenCampaigns(allNotificationIds);
  const showTooltip = shouldShowTooltip(allNotificationIds);

  const autoDismissRef = useRef(false);
  const [wobbleTick, setWobbleTick] = useState(0);

  // Auto-dismiss tooltip after showing
  useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => {
        autoDismissRef.current = true;
        markTooltipShown();
      }, 5000); // Auto-dismiss after 5 seconds

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [showTooltip, markTooltipShown]);

  // Re-trigger wobble every 3s while there are unseen campaigns
  useEffect(() => {
    if (!hasUnseen) {
      setWobbleTick(0);
      return undefined;
    }

    const interval = setInterval(() => {
      setWobbleTick((tick) => tick + 1);
    }, 3000);

    return () => clearInterval(interval);
  }, [hasUnseen]);

  const handleClick = () => {
    trackEvent(METRICS_EVENTS.EXCLUSIVE_NOTIFICATION_BELL_CLICKED, {
      campaignCount: campaignIds.length,
      hasUnseenCampaigns: hasUnseen,
      cancelledStreakCount: cancelledStreakIds.length,
    });
    onClick();
  };

  const handleTooltipDismiss = () => {
    markTooltipShown();
  };

  const hasCancelledStreaks = cancelledStreakIds.length > 0;
  const hasCampaigns = campaignIds.length > 0;

  const tooltipMessage =
    hasCancelledStreaks && hasCampaigns
      ? "You have new notifications!"
      : hasCancelledStreaks
        ? `You have ${cancelledStreakIds.length} autopilot${cancelledStreakIds.length > 1 ? "s" : ""} that need${cancelledStreakIds.length === 1 ? "s" : ""} attention`
        : `You have ${campaignIds.length} private campaign${campaignIds.length > 1 ? "s" : ""} available!`;

  return (
    <Tooltip
      open={showTooltip}
      onOpenChange={(open) => {
        if (open) return;
        if (autoDismissRef.current) {
          autoDismissRef.current = false;
          return;
        }
        handleTooltipDismiss();
      }}
    >
      <TooltipTrigger asChild>
        <Button
          variant="secondary"
          size="small"
          onClick={handleClick}
          className={cn("relative min-w-0 !px-2", className)}
          aria-label={`Notifications${hasUnseen ? " - new notifications available" : ""}`}
        >
          <Bell
            key={hasUnseen ? wobbleTick : "static"}
            size={20}
            strokeWidth={2.2}
            className={cn("text-cede-primary !h-[20px] !w-[20px]", hasUnseen && "animate-notification-wobble")}
          />
          {/* Unseen notifications: slides in + pops on appear, fades blurred on dismiss */}
          <span
            className="t-badge"
            data-open={hasUnseen ? "true" : "false"}
            style={{ top: "4px", right: "4px" }}
            aria-hidden="true"
          >
            <span className="t-badge-dot h-2 w-2 rounded-full bg-cede-error ring-2 ring-cede-bg-primary" />
          </span>
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[200px]">
        <p className="text-sm">{tooltipMessage}</p>
      </TooltipContent>
    </Tooltip>
  );
};
